import { Reveal, MaskText } from "./Reveal";
import { ReserveForm } from "./Reserve";
import { scrollToId } from "@/hooks/useLenis";

export const Founding50 = () => (
  <section id="founding" className="relative bg-sand py-24 sm:py-32 overflow-hidden">
    <div aria-hidden className="pointer-events-none absolute -top-8 -left-4 font-serif text-[30vw] leading-none text-ocean/[0.04] select-none">50</div>

    <div className="relative mx-auto max-w-[1300px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-y-14 gap-x-12">
        <div className="lg:col-span-6">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Launching January 5, 2027</span>
          </Reveal>
          <h2 className="mt-6 font-serif text-ocean tracking-[-0.02em] leading-[0.95] text-5xl sm:text-6xl lg:text-[5.4rem]">
            <MaskText lines={["Join the", <span key="f" className="italic text-cognac">Founding 50.</span>]} />
          </h2>
          <Reveal delay={0.12}>
            <p className="mt-6 font-serif italic text-xl sm:text-2xl text-ocean/85 leading-snug max-w-lg">
              Be one of the first 50 founders to build your roadmap with Brian Smith.
            </p>
          </Reveal>
          <div className="mt-8 max-w-xl font-sans text-base sm:text-lg text-ink/75 leading-relaxed space-y-5">
            <Reveal delay={0.15}>
              <p>
                <em>Create Your Business Roadmap in 7 Days</em> officially launches January 5, 2027. And
                we're doing something special for the people willing to go first.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p>
                The first 50 founders who reserve their spot will be invited to a private{" "}
                <span className="text-ocean font-medium">live kickoff session with Brian Smith</span> on
                January 5, 2027. Brian will introduce the roadmap philosophy, talk through how businesses
                evolve through different stages, answer questions, and help the Founding 50 begin
                thinking about the next chapter of their businesses.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <p className="mt-10 font-serif text-3xl sm:text-4xl lg:text-5xl text-ocean tracking-tight leading-[1.05]">
              50 seats.<br />One room.<br />
              <span className="italic text-cognac">Live with the founder of UGG.</span>
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:pl-6">
          <Reveal delay={0.1}>
            <div data-testid="founding-card" className="bg-ocean text-cream border border-white/10 p-8 sm:p-12">
              <div className="pb-8 border-b border-white/15">
                <div className="flex items-baseline justify-between gap-6">
                  <div className="font-sans text-[11px] uppercase tracking-[0.22em] text-cream/50">Program price</div>
                  <div data-testid="price-regular" className="font-serif text-2xl text-cream/45 line-through decoration-cognac/70">$497</div>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-6">
                  <div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.22em] text-cognac">Founding 50 · Pay now</div>
                    <div data-testid="price-founding" className="mt-1 font-serif text-5xl sm:text-6xl leading-none tracking-tight">$397</div>
                  </div>
                  <div className="border-l border-white/15 pl-6">
                    <div className="font-sans text-[11px] uppercase tracking-[0.22em] text-cream/60">Lock in · Pay Jan 1, 2027</div>
                    <div data-testid="price-later" className="mt-1 font-serif text-5xl sm:text-6xl leading-none tracking-tight text-cream/85">$447</div>
                  </div>
                </div>
                <p className="mt-5 font-sans text-xs text-cream/55 leading-relaxed">
                  Either way, your seat is saved the moment your card is confirmed. Pay now for the lowest price, or save your card today and nothing is charged until January 1, 2027.
                </p>
              </div>

              <ul className="mt-8 space-y-3 font-sans text-sm sm:text-[15px] text-cream/85">
                {[
                  "The full 7-day roadmap experience",
                  "Your personalized 12-month Founder Roadmap",
                  "The Founder Roadmap Guide, adapted to your stage",
                  "Unlimited Roadmap Resets as your business changes",
                  "Private live kickoff with Brian Smith · January 5, 2027",
                ].map((i, idx) => (
                  <li key={i} className={`flex items-start gap-3 ${idx === 4 ? "text-cream" : ""}`}>
                    <span className={`mt-2 h-1.5 w-1.5 shrink-0 ${idx === 4 ? "bg-cognac" : "bg-cream/40"}`} />
                    <span>{i}{idx === 4 && <span className="ml-2 inline-block whitespace-nowrap font-sans text-[10px] uppercase tracking-[0.18em] text-cognac">Founding 50 only</span>}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9">
                <ReserveForm source="founding" dark />
              </div>

              <button
                data-testid="founding-quiz-btn"
                onClick={() => scrollToId("assessment")}
                className="mt-6 group inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.18em] text-cream border-b border-cream/40 pb-1 hover:border-cognac hover:text-cognac transition-colors duration-300"
              >
                Take the stage quiz first
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>

              <p className="mt-8 pt-6 border-t border-white/10 font-sans text-[11px] text-cream/45 leading-relaxed">
                The 50-seat limit applies to the private live kickoff with Brian. The roadmap program itself remains open after launch. Pay-later cards are charged $447 on January 1, 2027; pay-now reservations are charged $397 today.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
